import React, { useState, useEffect } from 'react';
import { PLANS } from '../config/offers';
import styles from './Planos.module.css';
import MatrixCanvas from '../components/HomeComponents/MatrixCanvas';
import logoRed from '../assets/logo.png';
import { addUtmsToUrl } from '../utils/utm';

const CheckIcon = () => (
  <svg className={styles.checkIcon} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
  </svg>
);

const StarIcon = () => (
  <svg className={styles.starIcon} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const XIcon = () => (
  <svg className={styles.xIcon} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const TicketIcon = () => (
  <svg className={styles.ticketIcon} viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z" />
  </svg>
);

const Planos = () => {
  const plans = PLANS;
  const [progressWidth, setProgressWidth] = useState(0);
  const [slotsByPlanId, setSlotsByPlanId] = useState({});

  // Efeito da barrinha subindo ao exibir a página
  useEffect(() => {
    const t = setTimeout(() => setProgressWidth(65), 150);
    return () => clearTimeout(t);
  }, []);

  // Contador de vagas diminuindo aos poucos
  useEffect(() => {
    if (plans.length === 0) return;
    const initial = {};
    plans.forEach((p) => {
      if (p.slotsAvailable != null) initial[p.id] = p.slotsAvailable;
    });
    setSlotsByPlanId(initial);

    const interval = setInterval(() => {
      setSlotsByPlanId((prev) => {
        const next = { ...prev };
        let changed = false;
        Object.keys(next).forEach((id) => {
          const v = next[id];
          if (v > 1) {
            next[id] = v - 1;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 10000); // diminui 1 vaga a cada 10 segundos

    return () => clearInterval(interval);
  }, [plans]);

  function handleSelectPlan(plan) {
    if (!plan?.checkoutUrl) return;
    window.location.href = addUtmsToUrl(plan.checkoutUrl);
  }

  function formatPrice(value) {
    return value != null ? value.toFixed(2).replace('.', ',') : '';
  }

  return (
    <div className={styles.planosPage}>
      <MatrixCanvas />

      {/* Banner verde com bolinha de conexão */}
      <div className={styles.banner}>
        <span className={styles.bannerDot} aria-hidden />
        <div className={styles.bannerText}>
          <p className={styles.bannerTitle}>Sua identidade está preservada!</p>
          <p className={styles.bannerSubtitle}>Conexão Segura • Firewall ativo V4.2.1</p>
        </div>
      </div>

      {/* Header: logo + barra de progresso */}
      <header className={styles.header}>
        <img src={logoRed} alt="PinkStalker" className={styles.logo} />
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: `${progressWidth}%` }}
            role="progressbar"
            aria-valuenow={progressWidth}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </header>

      <main className={styles.main}>
        <div className={styles.container}>
          <h1 className={styles.title}>
            <TicketIcon />
            Escolha seu <span className={styles.highlight}>Plano</span>
          </h1>
          <p className={styles.subtitle}>
            Agora que sua conta já está com o <span className={styles.highlight}>firewall ativo</span> e sua <span className={styles.highlight}>identidade preservada</span>, selecione com qual plano você deseja prosseguir:
          </p>

          <div className={styles.cardsGrid}>
            {plans.map((plan, index) => {
              const featuresIncluded = Array.isArray(plan.featuresIncluded) ? plan.featuresIncluded : [];
              const featuresExcluded = Array.isArray(plan.featuresExcluded) ? plan.featuresExcluded : [];
              return (
                <div
                  key={plan.id}
                  className={`${styles.planCard} ${styles[`theme_${plan.colorTheme}`]} ${plan.badge ? styles.hasBadge : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {plan.badge && (
                    <div className={styles.cardBadgeWrap}>
                      <span className={styles.cardBadge}>
                        <span className={styles.cardBadgeShimmer} aria-hidden />
                        {plan.badge}
                      </span>
                    </div>
                  )}
                  <div className={styles.cardHeader}>
                    <span className={styles.cardHeaderBg} aria-hidden />
                    <h2 className={styles.planName}>{plan.name}</h2>
                    <p className={styles.planLabel}>Plano</p>
                  </div>
                  <div className={styles.cardInner}>
                  <div className={styles.priceBlock}>
                    {plan.originalPrice != null && (
                      <span className={styles.originalPrice}>R$ {formatPrice(plan.originalPrice)}</span>
                    )}
                    <span className={`${styles.currentPrice} ${styles[`priceTheme_${plan.colorTheme}`]}`}>R$ {formatPrice(plan.currentPrice)}</span>
                  </div>
                  <ul className={styles.featuresList}>
                    {featuresIncluded.map((f, i) => (
                      <li key={`inc-${i}`} className={styles.featureIncluded}>
                        <CheckIcon />
                        <span>{f}</span>
                      </li>
                    ))}
                    {featuresExcluded.map((f, i) => (
                      <li key={`exc-${i}`} className={styles.featureExcluded}>
                        <XIcon />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className={styles.ctaButton}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    <span className={styles.ctaButtonShimmer} aria-hidden />
                    {plan.badge === 'Melhor Custo Benefício' ? (
                      <>
                        <StarIcon /> {plan.buttonText} <StarIcon />
                      </>
                    ) : (
                      plan.buttonText
                    )}
                  </button>
                  {(plan.slotsAvailable != null || plan.paymentType) && (
                    <p className={styles.cardFooter}>
                      {plan.slotsAvailable != null && (
                        <span className={styles.slotsCount}>
                          {slotsByPlanId[plan.id] ?? plan.slotsAvailable} vagas
                        </span>
                      )}
                      {plan.slotsAvailable != null && plan.paymentType && ' • '}
                      {plan.paymentType}
                    </p>
                  )}
                  </div>
                </div>
              );
            })}
          </div>

          <footer className={styles.footer}>
            <img src={logoRed} alt="PinkStalker" className={styles.footerLogo} />
            <p className={styles.footerCopyright}>© 2025 PinkStalker — Todos os direitos reservados.®</p>
            <div className={styles.footerLinks}>
              <a href="#termos">Termos de Uso</a>
              <a href="#privacidade">Política de Privacidade</a>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default Planos;
